import { useScrollPosition } from '@n8tb1t/use-scroll-position'
import {
  AppBar,
  Button,
  Drawer,
  Icon,
  Menu,
  MenuItem,
  Paper,
  Toolbar,
  Typography,
} from '@pickupp/ui/core'
import { makeStyles, useTheme } from '@pickupp/ui/styles'
import { navigate } from '@reach/router'
import cx from 'classnames'
import PropTypes from 'prop-types'
import React, { useContext, useState } from 'react'

import { BROWSER, LANGUAGES } from '~/config'
import PickuppLogo from '~/src/assets/logo.png'
import Link from '~/src/components/Link'
import LinkButton from '~/src/components/LinkButton'
import { LocaleContext } from '~/src/contexts'
import { useIsMobile } from '~/src/hooks'
import { canTouch } from '~/src/services/utils'

import styles from './styles'

const useStyles = makeStyles(styles)

const HeaderLogo = () => {
  const theme = useTheme()
  const classes = useStyles()

  return (
    <Link to="/" className={classes.imageContainer}>
      <img
        loading="lazy"
        className={classes.image}
        src={PickuppLogo}
        width={theme.global.logoWidth}
        alt="Pickupp"
      />
    </Link>
  )
}

const DrawerMenuItems = ({ items, isMenuOpen }) => {
  const classes = useStyles()

  if (items?.length < 1 || !isMenuOpen) return null
  return (
    <div>
      {
        items.map(({ label, link }) => {
          if (!link) return null
          return (
            <Link
              key={link?.url}
              to={link?.url}
              target={link?.target}
              className={classes.navigationLink}
              event_name={link?.event_name}
              data-identity={link?.identity}
            >
              <Button
                fullWidth
                variant="text"
                className={cx(classes.menuButtonText, {
                  [classes.disabledLink]: !link?.url,
                  [classes.activeItem]: BROWSER && window.location.pathname.endsWith(link.url),
                })}
              >
                {label}
              </Button>
            </Link>
          )
        })
      }
    </div>
  )
}

DrawerMenuItems.defaultProps = {
  isMenuOpen: false,
}

DrawerMenuItems.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  isMenuOpen: PropTypes.bool,
}

const DropdownMenuItems = ({ items, isMenuOpen }) => {
  const classes = useStyles()

  if (items?.length < 1) return null
  return (
    <Paper
      className={cx(classes.dropdownItemsWrapper, {
        [classes.openMenuAnimation]: isMenuOpen,
      })}
      elevation={8}
    >
      {
        items.map(({ label, link }) => {
          if (!link) return null
          return (
            <div
              key={label}
              className={classes.menuItem}
            >
              <Link
                to={link?.url}
                target={link?.target}
                className={cx({ [classes.disabledLink]: !link?.url })}
                event_name={link?.event_name}
                data-identity={link?.identity}
              >
                <Typography
                  color="black"
                  className={cx(classes.linkText, {
                    [classes.activeItem]: BROWSER && window.location.pathname.endsWith(link?.url),
                  })}
                  title={label}
                >
                  {label}
                </Typography>
              </Link>
            </div>
          )
        })
      }
    </Paper>
  )
}

DropdownMenuItems.defaultProps = {
  isMenuOpen: false,
}

DropdownMenuItems.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  isMenuOpen: PropTypes.bool,
}

const HeaderNavigationsV2 = ({ drawer, navigations, drawerAdditionalMenuItems, rootClassName }) => {
  const classes = useStyles()
  const isTouchable = canTouch()

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [focusedMenu, setFocusedMenu] = useState(null)
  const isMobile = useIsMobile()

  const openDropdown = (menu) => {
    setFocusedMenu(menu)
  }

  const closeDropdown = () => {
    setFocusedMenu(null)
  }

  const toggleMenuItemsExpansion = (menu) => {
    //* IMPORTANT: Timeout is required to prevent click/touch event from selecting items inside the dropdown.
    setTimeout(() => {
      if (focusedMenu?.label === menu?.label) {
        closeDropdown()
        return
      }
      openDropdown(menu)
    }, 100)
  }

  const renderMenus = () => (
    navigations.map((navigation, i) => {
      const isMenuOpen = (focusedMenu?.label === navigation.label)
      const isActive = BROWSER && (
        (navigation.link && window.location.pathname.endsWith(navigation.link.url)) ||
        navigation.items.some((item) => item.link && window.location.pathname.endsWith(item.link.url))
      )
      const mainLink = (((!drawer && !isTouchable) || navigation.items.length < 1) && navigation.link?.url) || '#'

      return (
        <div
          key={i}
          className={classes.navigationLink}
          onMouseEnter={() => {
            if (drawer || isTouchable) return
            openDropdown(navigation)
          }}
          onMouseLeave={() => {
            if (drawer || isTouchable) return
            closeDropdown()
          }}
        >
          <div
            className={classes.menuDropdown}
            onMouseUp={() => {
              if (!drawer && !isTouchable) return
              toggleMenuItemsExpansion(navigation)
            }}
            role="button"
            aria-hidden="true"
          >
            <Link
              to={mainLink}
              target={isMobile ? null : navigation.link?.target}
              className={classes.navigationLink}
              event_name={navigation.link && navigation.link.event_name}
              data-identity={navigation.link && navigation.link.identity}
            >
              <div
                className={cx(classes.linkContent, {
                  [classes.buttonActive]: isActive,
                  [classes.menuFocus]: isMenuOpen,
                  [classes.alignCenter]: drawer,
                })}
              >
                <Typography
                  weight={700}
                  className={classes.navigationBarButton}
                >
                  {navigation.label}
                </Typography>
                {navigation.items?.length > 0 && (
                  <Icon
                    size="1.4rem"
                    name={isMenuOpen ? 'expand_less' : 'expand_more'}
                    className={classes.caret}
                  />
                )}
              </div>
            </Link>
          </div>
          {
            drawer ? (
              //* Drawer menu's items
              <DrawerMenuItems
                items={navigation.items}
                isMenuOpen={isMenuOpen}
              />
            ) : (
              //* Dropdown menu's items
              <DropdownMenuItems
                items={navigation.items}
                isMenuOpen={isMenuOpen}
              />
            )
          }
        </div>
      )
    })
  )

  return (
    <div className={rootClassName}>
      {
        drawer ? (
          //* Drawer Menu ----------------------------------
          <>
            <Icon
              name={isDrawerOpen ? 'close' : 'menu'}
              color="primary"
              className={classes.menuButton}
              onClick={() => { setIsDrawerOpen((prevIsOpen) => !prevIsOpen) }}
              disableRipple
              disableHover
            />
            <Drawer
              anchor="right"
              open={isDrawerOpen}
              onClose={() => { setIsDrawerOpen(false) }}
              className={classes.drawer}
            >
              <div className={classes.drawerContainer}>
                { renderMenus() }
                { drawerAdditionalMenuItems }
              </div>
            </Drawer>
          </>
        ) : (
          //* Dropdown Menu ----------------------------------
          <div className={classes.dropdownMenu}>
            {renderMenus()}
          </div>
        )
      }
    </div>
  )
}

HeaderNavigationsV2.defaultProps = {
  drawerAdditionalMenuItems: null,
  rootClassName: '',
  drawer: false,
}

HeaderNavigationsV2.propTypes = {
  navigations: PropTypes.arrayOf(PropTypes.object).isRequired,
  drawer: PropTypes.bool,
  drawerAdditionalMenuItems: PropTypes.element,
  rootClassName: PropTypes.string,
}

const HeaderLanguage = () => {
  const classes = useStyles()
  const [menuAnchor, setMenuAnchor] = useState(null)
  const { originalPath, label } = useContext(LocaleContext)

  const languages = LANGUAGES.filter((l) => {
    return !l.hide
  })

  if (languages.length === 1) return null

  const handleOpen = ({ currentTarget }) => {
    setMenuAnchor(currentTarget)
  }

  const handleClose = () => {
    setMenuAnchor(null)
  }

  return (
    <>
      <div
        className={classes.languageContainer}
        onClick={handleOpen}
        style={{ cursor: 'pointer' }}
      >
        <Icon
          name="language"
          size="1.4rem"
          color="primary"
        />
        <Typography
          variant="body2"
          color="primary"
          weight={700}
          className={classes.languageText}
        >
          {languages.find((lang) => lang.label === label).short_label}
        </Typography>
      </div>
      <Menu
        keepMounted
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        getContentAnchorEl={null}
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={handleClose}
      >
        {languages.map(({ key, label: languageLabel }) => (
          <MenuItem
            key={key}
            onClick={() => {
              handleClose()
              navigate(`/${key}${originalPath}`)
            }}
          >
            {languageLabel}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

const AuthButtons = ({ loginButton, signupLabel = 'SIGN UP', signUpButtons, className }) => {
  const classes = useStyles()
  const [menuAnchor, setMenuAnchor] = useState(null)

  const handleOpen = ({ currentTarget }) => {
    setMenuAnchor(currentTarget)
  }

  const handleClose = () => {
    setMenuAnchor(null)
  }

  if (!loginButton && !signUpButtons) return null

  return (
    <div className={cx(classes.navigationButton, classes.authButtons, className)}>
      {loginButton && (
        <LinkButton
          className={classes.button}
          to={loginButton.url}
          target={loginButton.target}
          event_name={loginButton.event_name}
          data-identity={loginButton.identity}
        >
          {loginButton.text}
        </LinkButton>
      )}
      {signUpButtons && (
        signUpButtons.length > 1 ? (
          <>
            <Button
              onClick={handleOpen}
              variant="outlined"
              className={classes.button}
            >
              {signupLabel}
            </Button>
            <Menu
              keepMounted
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              getContentAnchorEl={null}
              anchorEl={menuAnchor}
              open={!!menuAnchor}
              onClose={handleClose}
            >
              <div
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                {signUpButtons.map((button) => (
                  <div
                    key={button.event_name}
                    className={classes.buttonContainer}
                  >
                    <LinkButton
                      className={classes.signupButton}
                      to={button.url}
                      target={button.target}
                      event_name={button.event_name}
                      data-identity={button.identity}
                      variant="text"
                    >
                      {button.text}
                    </LinkButton>
                  </div>
                ))}
              </div>
            </Menu>
          </>
        ) : (
          <LinkButton
            className={classes.button}
            color="white"
            to={signUpButtons[0].url}
            target={signUpButtons[0].target}
            event_name={signUpButtons[0].event_name}
            data-identity={signUpButtons[0].identity}
          >
            {signUpButtons[0].text}
          </LinkButton>
        )
      )}
    </div>
  )
}

AuthButtons.propTypes = {
  signUpButtons: PropTypes.array.isRequired,
}

const HeaderV2 = ({
  navigations,
  login_button,
  sign_up_label,
  sign_up_buttons,
}) => {
  const [hide, setHide] = useState(false)
  useScrollPosition(({ prevPos, currPos }) => {
    const shouldHide = currPos.y < prevPos.y
    if (shouldHide !== hide) {
      setHide(shouldHide)
    }
  }, [hide])

  const classes = useStyles({ hide })

  return (
    <AppBar position="sticky" className={classes.appBar} elevation={0}>
      <Toolbar className={classes.toolbar}>
        <div className={classes.toolbarLeft}>
          <HeaderLogo />
        </div>
        <div className={classes.toolbarCenter}>
          <HeaderNavigationsV2 navigations={navigations} />
        </div>
        <div className={classes.toolbarRight}>
          <HeaderLanguage />
          <AuthButtons
            loginButton={login_button}
            signupLabel={sign_up_label}
            signUpButtons={sign_up_buttons}
            className={classes.desktopAuthButtons}
          />
          <HeaderNavigationsV2
            drawer
            rootClassName={classes.drawerMenu}
            navigations={navigations}
            drawerAdditionalMenuItems={(
              <AuthButtons
                loginButton={login_button}
                signupLabel={sign_up_label}
                signUpButtons={sign_up_buttons}
              />
            )}
          />
        </div>
      </Toolbar>
    </AppBar>
  )
}

HeaderV2.propTypes = {
  navigations: PropTypes.array.isRequired,
  login_button: PropTypes.object.isRequired,
  sign_up_label: PropTypes.string.isRequired,
  sign_up_buttons: PropTypes.array.isRequired,
}

export default HeaderV2
